// Profit & Drawdown Overview — fleet roll-up + per-account + per-bot, across
// day / week / month / quarter / year grains.
//
// Single source of truth is the Postgres engine veritas.account_period_metrics
// (per-(account, magic) per-period net, return %, and drawdown). This page only
// aggregates and renders — it never re-derives the numbers a second way, so the
// per-bot rows, account totals, and DB always agree (mirrors the S8 single-
// source rule). RLS does the scoping: an admin sees every account, a customer
// sees only their own, because the RPC is SECURITY INVOKER over closed_positions
// / account_snapshots.
//
// Honesty rails (Veritas adagium / lessons L4):
//   • Account drawdown = deposit-adjusted equity DD (authoritative).
//   • Per-bot drawdown = closed-trade-curve floor — understates martingale risk
//     (~3× in validation); always labelled as a floor.
//   • month / quarter / year are PARTIAL given ≤30 days of history → badged, not
//     dressed up as full-period figures.
//   • No fleet-summed drawdown: per-account DDs are path-dependent and not
//     synchronised, so summing them is meaningless. Shown per account instead.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, BarChart, ComposedChart, Bar, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { veritas, fetchAllPages } from '@/lib/supabase';
import { useCurrentUser } from '@/lib/auth';
import { cn, fmtMoney, fmtPct } from '@/lib/utils';
import { Panel, PanelHead, PanelBody, Kpi, Chip, Chips } from '@/components/ui';
import type { AccountLiveness } from '@/types/veritas';

// ── grain vocabulary ───────────────────────────────────────────────────────
const GRAINS = ['day', 'week', 'month', 'quarter', 'year'] as const;
type Grain = (typeof GRAINS)[number];
const GLAB: Record<Grain, string> = {
  day: 'Daily', week: 'Weekly', month: 'Monthly', quarter: 'Quarterly', year: 'Yearly',
};

// ── recharts theme (mirrors account-detail) ────────────────────────────────
const AXIS = { stroke: '#4b5563', fontSize: 10, fontFamily: 'JetBrains Mono' };
const GRID = '#1a2029';
const TooltipStyle = {
  backgroundColor: '#0f1115', border: '1px solid #232a35',
  borderRadius: 6, fontSize: 11, color: '#f3f4f6',
};
const C_GREEN = '#00ff88', C_RED = '#ef4444', C_BLUE = '#3b82f6', C_AMBER = '#f59e0b';

// ── row shape returned by veritas.account_period_metrics ───────────────────
type PeriodRow = {
  magic: number;
  period_start: string;            // 'YYYY-MM-DD'
  positions: number;
  net: number;
  equity_at_start: number | null;
  return_pct: number | null;
  bot_realized_dd_abs: number | null;
  bot_realized_dd_pct: number | null;
  acct_period_dd_pct: number | null;   // authoritative (account-level, deposit-adj)
  acct_period_dd_abs: number | null;
};

// numeric columns can arrive as strings from PostgREST — coerce.
const N = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : Number(v);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function coerce(r: any): PeriodRow {
  return {
    magic: Number(r.magic),
    period_start: String(r.period_start),
    positions: Number(r.positions ?? 0),
    net: Number(r.net ?? 0),
    equity_at_start: N(r.equity_at_start),
    return_pct: N(r.return_pct),
    bot_realized_dd_abs: N(r.bot_realized_dd_abs),
    bot_realized_dd_pct: N(r.bot_realized_dd_pct),
    acct_period_dd_pct: N(r.acct_period_dd_pct),
    acct_period_dd_abs: N(r.acct_period_dd_abs),
  };
}

const isEA = (m: number) => m !== 0;
const moneyClass = (v: number) => (v > 0 ? 'text-green' : v < 0 ? 'text-red' : 'text-text-dim');

// ── period labelling + partial detection ───────────────────────────────────
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function periodMeta(grain: Grain, iso: string): { label: string; partial: boolean; tip: string } {
  const start = new Date(iso + 'T00:00:00');
  const end = new Date(start);
  if (grain === 'day') end.setDate(end.getDate() + 1);
  else if (grain === 'week') end.setDate(end.getDate() + 7);
  else if (grain === 'month') end.setMonth(end.getMonth() + 1);
  else if (grain === 'quarter') end.setMonth(end.getMonth() + 3);
  else end.setFullYear(end.getFullYear() + 1);

  const shortHist = grain === 'month' || grain === 'quarter' || grain === 'year';
  const inProgress = new Date() < end;
  const partial = shortHist || inProgress;

  let label: string;
  if (grain === 'day') label = `${MON[start.getMonth()]} ${start.getDate()}`;
  else if (grain === 'week') label = `wk ${MON[start.getMonth()]} ${start.getDate()}`;
  else if (grain === 'month') label = `${MON[start.getMonth()]} ${start.getFullYear()}`;
  else if (grain === 'quarter') label = `Q${Math.floor(start.getMonth() / 3) + 1} ${start.getFullYear()}`;
  else label = `${start.getFullYear()}`;

  const tip = shortHist
    ? `≤30 days of history — not a full ${grain}`
    : inProgress ? `partial — ${grain} still in progress` : '';
  return { label, partial, tip };
}

// ── pure aggregations (mirror the SQL; sum reconciles by construction) ──────
type AcctPeriod = {
  period: string; positions: number; net: number;
  equity_at_start: number | null; return_pct: number | null; dd_pct: number | null;
};
function accountPeriods(rows: PeriodRow[]): AcctPeriod[] {
  const by = new Map<string, AcctPeriod>();
  for (const r of rows) {
    const p = by.get(r.period_start) ?? {
      period: r.period_start, positions: 0, net: 0,
      equity_at_start: r.equity_at_start, return_pct: null, dd_pct: r.acct_period_dd_pct,
    };
    p.net += r.net; p.positions += r.positions;
    if (p.equity_at_start == null) p.equity_at_start = r.equity_at_start;
    if (p.dd_pct == null) p.dd_pct = r.acct_period_dd_pct;   // account-level: same across magics
    by.set(r.period_start, p);
  }
  return [...by.values()]
    .map((p) => ({ ...p, return_pct: p.equity_at_start ? (100 * p.net) / p.equity_at_start : null }))
    .sort((a, b) => a.period.localeCompare(b.period));
}
function botPeriodsFor(rows: PeriodRow[], magic: number): AcctPeriod[] {
  return rows
    .filter((r) => r.magic === magic)
    .map((r) => ({
      period: r.period_start, positions: r.positions, net: r.net,
      equity_at_start: r.equity_at_start, return_pct: r.return_pct, dd_pct: r.bot_realized_dd_pct,
    }))
    .sort((a, b) => a.period.localeCompare(b.period));
}
type BotTotal = { magic: number; positions: number; net: number; ddFloor: number; last: string | null };
function botTotals(rows: PeriodRow[]): BotTotal[] {
  const by = new Map<number, BotTotal>();
  for (const r of rows) {
    const b = by.get(r.magic) ?? { magic: r.magic, positions: 0, net: 0, ddFloor: 0, last: null };
    b.net += r.net; b.positions += r.positions;
    if (r.bot_realized_dd_abs != null) b.ddFloor = Math.max(b.ddFloor, r.bot_realized_dd_abs);
    if (!b.last || r.period_start > b.last) b.last = r.period_start;
    by.set(r.magic, b);
  }
  return [...by.values()].sort((a, b) => b.net - a.net);
}

// For accounts flagged "one system" (many TradeFlow magics that are really one
// book): fold every EA magic into a single synthetic system row (magic -1) and
// keep Manual (magic 0) separate. Per-bot DD is intentionally dropped (ddFloor
// = NaN → rendered '—'); the meaningful figure is the account-level lifetime DD
// shown in the KPI strip. Summing per-bot DDs would overstate (path-dependent).
function collapseSystemBots(bots: BotTotal[]): BotTotal[] {
  const manual = bots.filter((b) => !isEA(b.magic));
  const ea = bots.filter((b) => isEA(b.magic));
  if (!ea.length) return manual;
  const sys: BotTotal = {
    magic: -1,
    positions: ea.reduce((s, b) => s + b.positions, 0),
    net: ea.reduce((s, b) => s + b.net, 0),
    ddFloor: NaN,
    last: ea.reduce<string | null>((m, b) => (b.last && (!m || b.last > m) ? b.last : m), null),
  };
  return [...manual, sys].sort((a, b) => b.net - a.net);
}

export default function PerformancePage() {
  const { user, loading: authLoading } = useCurrentUser();
  const [accounts, setAccounts] = useState<{ login: number; label: string }[]>([]);
  const [eaNames, setEaNames] = useState<Map<number, string>>(new Map());
  const [systemAccounts, setSystemAccounts] = useState<Set<number>>(new Set());
  const [adminScope, setAdminScope] = useState<'all' | 'mine'>('all');

  const [scope, setScope] = useState<string>('ALL');          // 'ALL' | account login string
  const [grain, setGrain] = useState<Grain>('week');          // most-meaningful grain today
  const [magic, setMagic] = useState<string>('ALL');          // 'ALL' | magic string (account view)

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache RPC results in a ref so concurrent loaders dedupe cleanly; bump
  // `version` to re-render when new data lands (avoids stale-closure bugs).
  const cacheRef = useRef<Record<string, PeriodRow[]>>({});
  const [version, setVersion] = useState(0);
  const ck = (acct: string, g: string) => `${acct}:${g}`;

  const botName = (m: number) =>
    m === -1 ? 'TradeFlow (system)'
    : m === 0 ? 'Manual'
    : eaNames.get(m) ?? `Unregistered (magic ${m})`;

  // ── account list (RLS-scoped) + EA name registry ──────────────────────────
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      const liveRes = await fetchAllPages<AccountLiveness>((from, to) => {
        let q = veritas
          .from('account_liveness')
          .select('*')
          .order('account_login', { ascending: true })
          .order('credential_id', { ascending: true });   // unique tiebreaker (fetchAllPages contract)
        if (adminScope === 'mine' && user?.id) q = q.eq('owner_user_id', user.id);
        return q.range(from, to);
      });
      if (cancelled) return;
      if (liveRes.error) { setError(liveRes.error.message); return; }
      const seen = new Set<number>();
      const list: { login: number; label: string }[] = [];
      for (const r of liveRes.data) {
        if (!seen.has(r.account_login)) { seen.add(r.account_login); list.push({ login: r.account_login, label: r.install_label }); }
      }
      setAccounts(list);

      const reg = await veritas
        .from('ea_registry')
        .select('magic_number,ea_name,ea_version,first_seen')
        .order('first_seen', { ascending: false });
      if (cancelled) return;
      const m = new Map<number, string>();
      for (const row of ((reg.data as Array<{ magic_number: number; ea_name: string; ea_version: string | null }>) ?? [])) {
        if (!m.has(row.magic_number)) m.set(row.magic_number, row.ea_version ? `${row.ea_name} (${row.ea_version})` : row.ea_name);
      }
      setEaNames(m);

      // Per-(user,account) "one system" flags — collapse the per-bot breakdown
      // for these accounts (RLS scopes the read to the caller's own rows).
      const meta = await veritas
        .from('account_user_metadata')
        .select('account_login,single_system');
      if (cancelled) return;
      const sys = new Set<number>();
      for (const r of ((meta.data as Array<{ account_login: number; single_system: boolean }> | null) ?? [])) {
        if (r.single_system) sys.add(r.account_login);
      }
      setSystemAccounts(sys);
    })();
    return () => { cancelled = true; };
  }, [adminScope, user?.id, authLoading]);

  const accountLogins = useMemo(() => accounts.map((a) => String(a.login)), [accounts]);

  // ── load the period data needed for the current view (selected grain + year
  //    grain for the authoritative lifetime DD), cached per (account, grain) ──
  //
  // Single bulk RPC per grain (account_period_metrics_fleet) instead of fanning
  // out one RPC per account. 14+ accounts × 2 grains × ~1.6s mean (σ=1.9s) used
  // to overshoot the authenticated statement_timeout=8s on heavy accounts and
  // saturate the PostgREST pool. The bulk wrapper runs ~2s for the whole fleet
  // at day-grain — comfortably under 8s — and reduces 28+ round-trips to 2.
  // See tasks/performance-page-perf-findings.md.
  useEffect(() => {
    if (authLoading) return;
    const accts = scope === 'ALL' ? accountLogins : [scope];
    if (!accts.length) return;
    let cancelled = false;
    (async () => {
      const grains: Grain[] = grain === 'year' ? ['year'] : [grain, 'year'];
      // Group missing (account, grain) pairs by grain so each grain fires ONE bulk call.
      const missingByGrain = new Map<Grain, string[]>();
      for (const a of accts) {
        for (const g of grains) {
          if (!cacheRef.current[ck(a, g)]) {
            const list = missingByGrain.get(g) ?? [];
            list.push(a);
            missingByGrain.set(g, list);
          }
        }
      }
      if (!missingByGrain.size) return;
      setLoading(true); setError(null);
      const res = await Promise.all(
        [...missingByGrain.entries()].map(async ([g, gAccts]) => {
          const { data, error: err } = await veritas.rpc('account_period_metrics_fleet', {
            p_account_logins: gAccts.map(Number),
            p_grain: g,
          });
          // Split the multi-account response back into per-(acct, grain) buckets
          // so the existing cache shape + all downstream view-models stay byte-
          // identical to the prior per-account RPC contract. Accounts with no
          // closed positions land as [] (same as before).
          const byAcct = new Map<string, PeriodRow[]>();
          for (const a of gAccts) byAcct.set(a, []);
          for (const r of (((data as unknown[]) ?? []) as Array<Record<string, unknown>>)) {
            const k = String(r.account_login);
            const list = byAcct.get(k);
            if (list) list.push(coerce(r));
          }
          return { g, byAcct, err };
        }),
      );
      if (cancelled) return;
      let firstErr: string | null = null;
      for (const r of res) {
        for (const [a, rows] of r.byAcct) cacheRef.current[ck(a, r.g)] = rows;
        if (r.err && !firstErr) firstErr = r.err.message;
      }
      if (firstErr) setError(firstErr);
      setVersion((v) => v + 1);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, grain, accountLogins, authLoading]);

  // reset bot filter whenever the scope changes
  useEffect(() => { setMagic('ALL'); }, [scope]);

  // lifetime account DD = the single 2026 bucket of the YEAR grain (deposit-adj)
  const lifetimeDD = (acct: string): number | null => {
    const yr = cacheRef.current[ck(acct, 'year')];
    if (!yr) return null;
    const row = yr.find((r) => r.acct_period_dd_pct != null);
    return row ? row.acct_period_dd_pct : null;
  };

  // ── view models ────────────────────────────────────────────────────────
  const fleet = useMemo(() => {
    if (scope !== 'ALL') return null;
    const perAcct = accountLogins
      .map((login) => ({ login, rows: cacheRef.current[ck(login, grain)] ?? [] }))
      .filter((x) => x.rows.length > 0);

    // by period (EA vs manual; worst single-account DD that period)
    const byP = new Map<string, { period: string; ea: number; manual: number; total: number; positions: number; ddByAcct: Map<string, number> }>();
    for (const { login, rows } of perAcct) {
      for (const r of rows) {
        const p = byP.get(r.period_start) ?? { period: r.period_start, ea: 0, manual: 0, total: 0, positions: 0, ddByAcct: new Map() };
        if (isEA(r.magic)) p.ea += r.net; else p.manual += r.net;
        p.total += r.net; p.positions += r.positions;
        if (r.acct_period_dd_pct != null) p.ddByAcct.set(login, r.acct_period_dd_pct);
        byP.set(r.period_start, p);
      }
    }
    const byPeriod = [...byP.values()]
      .map((p) => ({ period: p.period, ea: p.ea, manual: p.manual, total: p.total, positions: p.positions,
        worstDD: p.ddByAcct.size ? Math.max(...p.ddByAcct.values()) : null }))
      .sort((a, b) => a.period.localeCompare(b.period));

    // by account (lifetime)
    const byAccount = perAcct.map(({ login, rows }) => {
      let ea = 0, manual = 0, total = 0, positions = 0;
      for (const r of rows) { if (isEA(r.magic)) ea += r.net; else manual += r.net; total += r.net; positions += r.positions; }
      return { login, ea, manual, total, positions, lifeDD: lifetimeDD(login) };
    }).sort((a, b) => b.total - a.total);

    const eaNet = byAccount.reduce((s, a) => s + a.ea, 0);
    const manNet = byAccount.reduce((s, a) => s + a.manual, 0);
    const totalNet = byAccount.reduce((s, a) => s + a.total, 0);
    const worstDD = byAccount.reduce((m, a) => Math.max(m, a.lifeDD ?? 0), 0);
    const nBots = new Set(perAcct.flatMap(({ login, rows }) => rows.filter((r) => isEA(r.magic)).map((r) => `${login}:${r.magic}`))).size;

    return { byPeriod, byAccount, eaNet, manNet, totalNet, worstDD, nAccts: byAccount.length, nBots };
  }, [scope, grain, accountLogins, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const acct = useMemo(() => {
    if (scope === 'ALL') return null;
    const rows = cacheRef.current[ck(scope, grain)] ?? [];
    const flagged = systemAccounts.has(Number(scope));
    const bots = flagged ? collapseSystemBots(botTotals(rows)) : botTotals(rows);
    const single = magic !== 'ALL';
    const series = single ? botPeriodsFor(rows, Number(magic)) : accountPeriods(rows);
    const totalNet = bots.reduce((s, b) => s + b.net, 0);
    const eaNet = bots.filter((b) => isEA(b.magic)).reduce((s, b) => s + b.net, 0);
    const manNet = bots.filter((b) => !isEA(b.magic)).reduce((s, b) => s + b.net, 0);
    return { rows, bots, single, series, totalNet, eaNet, manNet, lifeDD: lifetimeDD(scope) };
  }, [scope, grain, magic, version, systemAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3.5">
      {/* controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">Scope</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="rounded-md border border-line bg-bg-1 px-3 py-2 text-[12px] text-text num"
          >
            <option value="ALL">All accounts (fleet roll-up)</option>
            {accounts.map((a) => (
              <option key={a.login} value={String(a.login)}>{a.login} · {a.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">Grain</span>
          <Chips>
            {GRAINS.map((g) => (
              <Chip key={g} active={grain === g} onClick={() => setGrain(g)}>{GLAB[g]}</Chip>
            ))}
          </Chips>
        </div>

        {user?.isAdmin && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">Accounts</span>
            <Chips>
              <Chip active={adminScope === 'all'} onClick={() => setAdminScope('all')}>All</Chip>
              <Chip active={adminScope === 'mine'} alt onClick={() => setAdminScope('mine')}>Mine</Chip>
            </Chips>
          </div>
        )}

        <span className="ml-auto text-[11px] text-text-dim">
          {loading ? 'Loading…' : `Source: account_period_metrics · ${GLAB[grain]}`}
        </span>
      </div>

      {/* honesty banner */}
      <div className="rounded-[10px] border border-line bg-bg-1 px-[18px] py-3 text-[11.5px] leading-relaxed text-text-mid">
        <span className="font-semibold text-text">Descriptive, not advice.</span>{' '}
        Drawdown is shown two ways — <span className="text-text">account-level</span> (deposit-adjusted equity, authoritative)
        and <span className="text-text">per-bot</span> (closed-trade floor, which understates a martingale’s true risk).
        History is short (longest account ≈30 days), so <span className="text-text">monthly / quarterly / yearly periods are partial</span> and badged as such.
      </div>

      {error && (
        <div className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>
      )}

      {scope === 'ALL'
        ? <FleetView fleet={fleet} grain={grain} loading={loading} onPickAccount={setScope} />
        : <AccountView acct={acct} grain={grain} scope={scope} magic={magic} setMagic={setMagic} botName={botName} loading={loading} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Fleet view
// ════════════════════════════════════════════════════════════════════════
type FleetVM = {
  byPeriod: { period: string; ea: number; manual: number; total: number; positions: number; worstDD: number | null }[];
  byAccount: { login: string; ea: number; manual: number; total: number; positions: number; lifeDD: number | null }[];
  eaNet: number; manNet: number; totalNet: number; worstDD: number; nAccts: number; nBots: number;
};

function FleetView({ fleet, grain, loading, onPickAccount }: {
  fleet: FleetVM | null; grain: Grain; loading: boolean; onPickAccount: (login: string) => void;
}) {
  if (!fleet || fleet.byAccount.length === 0) {
    return <EmptyState loading={loading} />;
  }
  const chartData = fleet.byPeriod.map((p) => ({ ...p, label: periodMeta(grain, p.period).label }));
  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Kpi label="Realized P&L · fleet" tone={fleet.totalNet < 0 ? 'red' : 'green'} value={<span className={moneyClass(fleet.totalNet)}>{fmtMoney(fleet.totalNet, '')}</span>} />
        <Kpi label="EA bots (magic ≠ 0)" tone="blue" value={<span className={moneyClass(fleet.eaNet)}>{fmtMoney(fleet.eaNet, '')}</span>} sub="automated" />
        <Kpi label="Manual (magic 0)" tone="amber" value={<span className={moneyClass(fleet.manNet)}>{fmtMoney(fleet.manNet, '')}</span>} sub="discretionary" />
        <Kpi label="Worst account DD" tone="red" value={fmtPct(fleet.worstDD)} sub="deposit-adj · lifetime" />
        <Kpi label="Accounts · EA installs" tone="green" value={`${fleet.nAccts} · ${fleet.nBots}`} />
      </div>

      <Panel>
        <PanelHead title={<>Fleet net P&L <span className="text-text-dim">{GLAB[grain]} · EA vs manual</span></>} />
        <PanelBody>
          <div className="h-[240px] sm:h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} stackOffset="sign">
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={AXIS} interval="preserveStartEnd" />
                <YAxis tick={AXIS} width={64} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={TooltipStyle} formatter={(v: number) => fmtMoney(v, '')} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ea" name="EA bots" stackId="a" fill={C_BLUE} radius={[2, 2, 0, 0]} />
                <Bar dataKey="manual" name="Manual" stackId="a" fill={C_AMBER} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11px] text-text-dim">
            A fleet-wide drawdown number is intentionally omitted — per-account drawdowns are path-dependent and not synchronised, so summing them would be meaningless. See per-account DD below.
          </p>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead title={<>Fleet by period <span className="text-text-dim">{GLAB[grain]}</span></>} />
        <PanelBody flush>
          <div className="thin-scroll overflow-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead><tr>
                <Th>Period</Th><Th right>Trades</Th><Th right>EA net</Th><Th right>Manual net</Th><Th right>Total net</Th><Th right>Worst acct DD</Th>
              </tr></thead>
              <tbody>
                {[...fleet.byPeriod].reverse().map((p) => {
                  const meta = periodMeta(grain, p.period);
                  return (
                    <tr key={p.period} className="border-b border-line-soft">
                      <td className="px-4 py-2.5">{meta.label}{meta.partial && <PartialBadge tip={meta.tip} />}</td>
                      <Td>{p.positions}</Td>
                      <Td><span className={moneyClass(p.ea)}>{fmtMoney(p.ea, '')}</span></Td>
                      <Td><span className={moneyClass(p.manual)}>{fmtMoney(p.manual, '')}</span></Td>
                      <Td><span className={moneyClass(p.total)}>{fmtMoney(p.total, '')}</span></Td>
                      <Td>{p.worstDD == null ? '—' : fmtPct(p.worstDD)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead title={<>By account <span className="text-text-dim">lifetime · click to drill in</span></>} />
        <PanelBody flush>
          <div className="thin-scroll overflow-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead><tr>
                <Th>Account</Th><Th right>Trades</Th><Th right>EA net</Th><Th right>Manual net</Th><Th right>Total net</Th><Th right>Lifetime DD</Th>
              </tr></thead>
              <tbody>
                {fleet.byAccount.map((a) => (
                  <tr key={a.login} onClick={() => onPickAccount(a.login)} className="cursor-pointer border-b border-line-soft transition-colors hover:bg-white/[0.02]">
                    <td className="num px-4 py-2.5 font-medium text-text">{a.login}</td>
                    <Td>{a.positions}</Td>
                    <Td><span className={moneyClass(a.ea)}>{fmtMoney(a.ea, '')}</span></Td>
                    <Td><span className={moneyClass(a.manual)}>{fmtMoney(a.manual, '')}</span></Td>
                    <Td><span className={moneyClass(a.total)}>{fmtMoney(a.total, '')}</span></Td>
                    <Td>{a.lifeDD == null ? '—' : fmtPct(a.lifeDD)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Account view
// ════════════════════════════════════════════════════════════════════════
type AcctVM = {
  rows: PeriodRow[];
  bots: BotTotal[];
  single: boolean;
  series: AcctPeriod[];
  totalNet: number; eaNet: number; manNet: number; lifeDD: number | null;
};

function AccountView({ acct, grain, scope, magic, setMagic, botName, loading }: {
  acct: AcctVM | null; grain: Grain; scope: string; magic: string;
  setMagic: (m: string) => void; botName: (m: number) => string; loading: boolean;
}) {
  if (!acct || (acct.bots.length === 0 && acct.series.length === 0)) {
    return <EmptyState loading={loading} />;
  }
  const single = acct.single;
  const chartData = acct.series.map((p) => ({
    label: periodMeta(grain, p.period).label, net: p.net, dd: p.dd_pct,
  }));
  const ddLabel = single ? 'Bot DD % (floor)' : 'Account DD %';

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Realized P&L" tone={acct.totalNet < 0 ? 'red' : 'green'} value={<span className={moneyClass(acct.totalNet)}>{fmtMoney(acct.totalNet, '')}</span>} />
        <Kpi label="EA bots" tone="blue" value={<span className={moneyClass(acct.eaNet)}>{fmtMoney(acct.eaNet, '')}</span>} />
        <Kpi label="Manual" tone="amber" value={<span className={moneyClass(acct.manNet)}>{fmtMoney(acct.manNet, '')}</span>} />
        <Kpi label="Lifetime max DD" tone="red" value={fmtPct(acct.lifeDD)} sub="deposit-adj · account" />
      </div>

      {/* bot picker */}
      <div className="flex flex-wrap gap-2">
        <BotChip active={magic === 'ALL'} onClick={() => setMagic('ALL')}>All bots (account total)</BotChip>
        {acct.bots.filter((b) => b.magic !== -1).map((b) => (
          <BotChip key={b.magic} active={magic === String(b.magic)} onClick={() => setMagic(String(b.magic))}>
            {botName(b.magic)}
          </BotChip>
        ))}
      </div>

      <Panel>
        <PanelHead title={<>{single ? botName(Number(magic)) : `Account ${scope}`} <span className="text-text-dim">{GLAB[grain]} net + drawdown</span></>} />
        <PanelBody>
          <div className="h-[240px] sm:h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={AXIS} interval="preserveStartEnd" />
                <YAxis yAxisId="left" tick={AXIS} width={56} tickFormatter={(v) => fmtMoney(v, '').replace('.00', '')} />
                <YAxis yAxisId="right" orientation="right" tick={AXIS} width={42} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={TooltipStyle}
                  formatter={(val, _name, item) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const k = (item as any)?.dataKey;
                    return k === 'dd' ? fmtPct(Number(val)) : fmtMoney(Number(val), '');
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="net" name="Net P&L" radius={[2, 2, 0, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.net >= 0 ? C_GREEN : C_RED} />)}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="dd" name={ddLabel} stroke={C_AMBER} strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11px] text-text-dim">
            {single
              ? 'Per-bot drawdown is a closed-trade-curve floor — it excludes floating/open risk and understates a martingale’s true drawdown.'
              : 'Account drawdown is the deposit-adjusted equity drawdown within each period (authoritative — same basis as account_max_drawdown).'}
          </p>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead title={<>{single ? botName(Number(magic)) : 'Account total'} by period <span className="text-text-dim">{GLAB[grain]}</span></>} />
        <PanelBody flush>
          <div className="thin-scroll overflow-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead><tr>
                <Th>Period</Th><Th right>Trades</Th><Th right>Net P&L</Th><Th right>Return %</Th><Th right>{single ? 'Bot DD % (floor)' : 'Max DD %'}</Th>
              </tr></thead>
              <tbody>
                {[...acct.series].reverse().map((p) => {
                  const meta = periodMeta(grain, p.period);
                  return (
                    <tr key={p.period} className="border-b border-line-soft">
                      <td className="px-4 py-2.5">{meta.label}{meta.partial && <PartialBadge tip={meta.tip} />}</td>
                      <Td>{p.positions}</Td>
                      <Td><span className={moneyClass(p.net)}>{fmtMoney(p.net, '')}</span></Td>
                      <Td>{p.return_pct == null ? '—' : fmtPct(p.return_pct)}</Td>
                      <Td>{p.dd_pct == null ? '—' : fmtPct(p.dd_pct)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead title={<>By bot <span className="text-text-dim">lifetime · DD is a closed-curve floor</span></>} />
        <PanelBody flush>
          <div className="thin-scroll overflow-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead><tr>
                <Th>Bot</Th><Th right>Trades</Th><Th right>Net P&L</Th><Th right>DD floor</Th><Th right>Last active</Th>
              </tr></thead>
              <tbody>
                {acct.bots.map((b) => {
                  const sys = b.magic === -1;
                  return (
                  <tr
                    key={b.magic}
                    onClick={sys ? undefined : () => setMagic(String(b.magic))}
                    className={cn(
                      'border-b border-line-soft transition-colors',
                      sys ? '' : 'cursor-pointer hover:bg-white/[0.02]',
                    )}
                  >
                    <td className="px-4 py-2.5 text-text">{botName(b.magic)}</td>
                    <Td>{b.positions}</Td>
                    <Td><span className={moneyClass(b.net)}>{fmtMoney(b.net, '')}</span></Td>
                    <Td>
                      {Number.isNaN(b.ddFloor)
                        ? <span className="text-text-dim" title="Per-bot drawdown isn’t meaningful for a folded system — see the account-level Lifetime max DD above.">—</span>
                        : <span className="text-text-mid">{fmtMoney(-Math.abs(b.ddFloor), '')}</span>}
                    </Td>
                    <Td>{b.last ? periodMeta('day', b.last).label : '—'}</Td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

// ── small shared bits ──────────────────────────────────────────────────────
function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th className={cn(
      'sticky top-0 z-[1] whitespace-nowrap border-b border-line bg-bg-2 px-4 py-2.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim',
      right ? 'text-right' : 'text-left',
    )}>{children}</th>
  );
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="num px-4 py-2.5 text-right text-[11.5px]">{children}</td>;
}
function PartialBadge({ tip }: { tip: string }) {
  return (
    <span title={tip} className="ml-2 inline-block rounded-full bg-amber/15 px-2 py-[1px] text-[9px] font-bold uppercase tracking-[0.08em] text-amber">
      partial
    </span>
  );
}
function BotChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-[5px] text-[11px] font-semibold transition-colors',
        active ? 'border-blue bg-blue text-white' : 'border-line bg-bg-1 text-text-mid hover:text-text',
      )}
    >{children}</button>
  );
}
function EmptyState({ loading }: { loading: boolean }) {
  return (
    <Panel>
      <PanelBody>
        <div className="px-4 py-12 text-center text-[12px] text-text-dim">
          {loading ? 'Loading period metrics…' : 'No closed-trade history for this selection yet.'}
        </div>
      </PanelBody>
    </Panel>
  );
}
